const DEFAULT_MODE_OPTIONS = [
  {
    mode_id: 'senior_advocate_prep',
    label: 'Senior advocate prep',
    description: 'Compact answers, best authorities, weak spots, and the bench-facing pressure points worth rehearsing.',
    section_ids: [
      'hearing_objective',
      'core_issues',
      'strongest_authorities',
      'weakest_points',
      'likely_bench_questions',
      'rebuttal_notes',
    ],
  },
  {
    mode_id: 'junior_prep_checklist',
    label: 'Junior prep checklist',
    description: 'Chronology anchors, record gaps, and concrete pre-hearing tasks that should be closed before the file is called.',
    section_ids: [
      'core_issues',
      'chronology',
      'weakest_points',
      'next_steps_before_hearing',
    ],
  },
  {
    mode_id: 'oral_submissions_outline',
    label: 'Oral submissions outline',
    description: 'A clean hearing opening, issue sequence, authorities, and fallback answers if the bench narrows the ask.',
    section_ids: [
      'hearing_objective',
      'oral_submissions_outline',
      'strongest_authorities',
      'rebuttal_notes',
    ],
  },
  {
    mode_id: 'last_night_hearing_cram',
    label: 'Last-night hearing cram',
    description: 'The shortest useful briefing: the ask, chronology anchor, best authority, hardest question, and next morning tasks.',
    section_ids: [
      'hearing_objective',
      'chronology',
      'strongest_authorities',
      'likely_bench_questions',
      'next_steps_before_hearing',
    ],
  },
  {
    mode_id: 'bench_question_prep',
    label: 'Bench question prep',
    description: 'Likely interruptions, where the file is vulnerable, and the rebuttal notes you want ready at counsel table.',
    section_ids: [
      'weakest_points',
      'likely_bench_questions',
      'rebuttal_notes',
      'strongest_authorities',
    ],
  },
];

function normalizeText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function fallbackItemsFromContent(content) {
  return String(content || '')
    .split('\n')
    .map((line) => normalizeText(line.replace(/^\s*-\s*/, '')))
    .filter(Boolean)
    .map((text) => ({ text, detail: text, provenance_labels: [], citations: [] }));
}

function normalizeSection(section = {}) {
  const items = Array.isArray(section.items) && section.items.length
    ? section.items
        .map((item) => ({
          text: normalizeText(item?.text || item?.title || item?.description),
          detail: normalizeText(item?.detail || item?.description || item?.text || item?.title),
          provenance_labels: Array.isArray(item?.provenance_labels) ? item.provenance_labels.filter(Boolean) : [],
          citations: Array.isArray(item?.citations) ? item.citations.filter(Boolean) : [],
        }))
        .filter((item) => item.text)
    : fallbackItemsFromContent(section.content);

  return {
    section_id: normalizeText(section.section_id),
    title: normalizeText(section.title) || 'Section',
    content: String(section.content || ''),
    items,
    citations: Array.isArray(section.citations) ? section.citations.filter(Boolean) : [],
    provenance_labels: Array.isArray(section.provenance_labels) ? section.provenance_labels.filter(Boolean) : [],
    user_edited: Boolean(section.user_edited),
    updated_at: section.updated_at || '',
  };
}

export function normalizeHearingPrepPack(pack = {}) {
  const sections = Array.isArray(pack.sections) ? pack.sections.map(normalizeSection) : [];
  const modeOptions = Array.isArray(pack.mode_options) && pack.mode_options.length
    ? pack.mode_options
    : DEFAULT_MODE_OPTIONS;

  return {
    hearing_pack_id: pack.hearing_pack_id || '',
    headline: normalizeText(pack.headline),
    matter_name: normalizeText(pack.matter_name),
    default_mode: normalizeText(pack.default_mode) || 'senior_advocate_prep',
    mode_options: modeOptions,
    source_summary: Array.isArray(pack.source_summary) ? pack.source_summary.filter((item) => item?.label && item?.count) : [],
    sections,
  };
}

export function buildHearingPrepView(pack = {}, preferredMode = '') {
  const normalized = normalizeHearingPrepPack(pack);
  const sectionMap = new Map(normalized.sections.map((section) => [section.section_id, section]));
  const activeMode = normalized.mode_options.find((item) => item.mode_id === preferredMode)
    || normalized.mode_options.find((item) => item.mode_id === normalized.default_mode)
    || normalized.mode_options[0]
    || DEFAULT_MODE_OPTIONS[0];

  const sections = Array.isArray(activeMode?.section_ids) && activeMode.section_ids.length
    ? activeMode.section_ids.map((sectionId) => sectionMap.get(sectionId)).filter(Boolean)
    : normalized.sections;

  return {
    ...normalized,
    activeMode,
    sections,
  };
}
